import { PipeTransform, Pipe } from "@angular/core";
import { environment } from "src/environments/environment";
import { DomSanitizer } from "@angular/platform-browser";
import { ListingMedia } from "./interfaces/listing-media";
import { version as pdfjsVersion } from "pdfjs-dist";
import { QueryString } from "./interfaces/query-string";

@Pipe({
	name: "listingMediaUrl",
})
export class ListingMediaUrlPipe implements PipeTransform {
	constructor(private domSanitizer: DomSanitizer) {}

	transform(
		mediaOrFileNameOrUrl: ListingMedia | string,
		size: string = "small",
		cacheBuster?: string,
		error?: boolean
	): any {
		// If an error was reported, return null instead.
		// This can be used to load an alternate image.
		if (error || !mediaOrFileNameOrUrl) {
			return null;
		}

		// Construct the cacheBuster query string if necessary.
		const cacheBusterQueryString: QueryString = !!cacheBuster
			? QueryString(`?t=${cacheBuster}`)
			: "";

		// If a string was passed, use that. Otherwise, use the mediaFileName if present, otherwise use the mediaURL.
		const fileNameOrUrl =
			typeof mediaOrFileNameOrUrl === "string"
				? mediaOrFileNameOrUrl
				: mediaOrFileNameOrUrl.mediaFileName || mediaOrFileNameOrUrl.mediaURL;

		// If this is a YouTube URL and we can extract an ID, then use that to get the thumbnail or embed URL.
		const youTubeId = this.getYouTubeId(fileNameOrUrl);
		if (!!youTubeId) {
			const url =
				size === "thumb" ? this.getYouTubeThumbnail(youTubeId) : this.getYouTubeEmbedUrl(youTubeId);
			return this.domSanitizer.bypassSecurityTrustResourceUrl(url);
		}

		// Make sure this URL has // at the start. If it doesn't, prefix it with the media display URL.
		const url =
			fileNameOrUrl.indexOf("//") > -1
				? fileNameOrUrl
				: environment.endpoints.mediaDisplay + "/" + fileNameOrUrl;

		// This size exists purely as an easy way to use the pipe to get a valid resource URL.
		if (size === "resource") {
			let suffix = "";
			if (typeof mediaOrFileNameOrUrl !== "string" && mediaOrFileNameOrUrl.mediaType === "rtf") {
				suffix = "/html";
			}
			return this.domSanitizer.bypassSecurityTrustResourceUrl(url + suffix);
		}
		if (size === "pdf") {
			return {
				standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjsVersion}/standard_fonts`,
				url: url + "/" + cacheBusterQueryString,
			};
		}
		return url + "/" + size + cacheBusterQueryString;
	}

	getYouTubeId(url: string): string | undefined {
		const cleanUrl = url.replace(/(>|<)/gi, "");

		const regexForYoutube =
			/(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S*?[?&]v=|embed\/|v\/|watch\?.*?v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
		const match = cleanUrl.match(regexForYoutube);
		return match ? match[1] : null;
	}

	getYouTubeEmbedUrl(id: string): string {
		return "https://www.youtube.com/embed/" + id + "?modestbranding=1";
	}

	getYouTubeThumbnail(id: string): string {
		return "https://i.ytimg.com/vi/" + id + "/hqdefault.jpg";
	}
}
